import React, { useState } from 'react';
import { useDeleteApiKey } from '../../hooks/useApiKeys';
import { ApiKeyType } from '../../types';

interface ApiKey {
  keyId: string;
  keyType: ApiKeyType;
  nickname: string;
  isDefault: boolean;
  createdAt: Date;
  lastUsedAt?: Date;
}

interface ApiKeyListProps {
  keys: ApiKey[];
}

interface KeyTypeInfo {
  icon: string;
  label: string;
  required: boolean;
}

const KEY_TYPE_INFO: Record<ApiKeyType, KeyTypeInfo> = {
  claude: { icon: '🤖', label: 'Claude', required: true },
  tavily: { icon: '🔍', label: 'Tavily', required: true },
  gemini: { icon: '✨', label: 'Gemini', required: false },
};

/**
 * List of user's API keys
 * Shows key info and allows deletion
 */
export const ApiKeyList: React.FC<ApiKeyListProps> = ({ keys }) => {
  const [deletingKeyId, setDeletingKeyId] = useState<string | null>(null);
  const { deleteKey, loading: deleting } = useDeleteApiKey();

  const handleDelete = async (keyId: string, nickname: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete the API key "${nickname}"?\n\nThis action cannot be undone.`
    );

    if (!confirmed) {
      return;
    }

    setDeletingKeyId(keyId);
    try {
      await deleteKey(keyId);
    } catch (err) {
      console.error('Failed to delete key:', err);
    } finally {
      setDeletingKeyId(null);
    }
  };

  const formatDate = (date?: Date) => {
    if (!date) return 'Never';
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(date);
  };

  // Group keys by type
  const groupedKeys = keys.reduce((acc, key) => {
    if (!acc[key.keyType]) {
      acc[key.keyType] = [];
    }
    acc[key.keyType].push(key);
    return acc;
  }, {} as Record<ApiKeyType, ApiKey[]>);

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-gray-900">Your API Keys</h3>

      {/* Render each key type group */}
      {(['claude', 'tavily', 'gemini'] as ApiKeyType[]).map((type) => {
        const typeKeys = groupedKeys[type] || [];
        const typeInfo = KEY_TYPE_INFO[type];

        if (typeKeys.length === 0) return null;

        return (
          <div key={type} className="space-y-3">
            <div className="flex items-center space-x-2">
              <span className="text-xl">{typeInfo.icon}</span>
              <h4 className="text-md font-semibold text-gray-900">
                {typeInfo.label} API Keys ({typeKeys.length})
              </h4>
            </div>

            <div className="space-y-3">
              {typeKeys.map((key) => (
                <div
                  key={key.keyId}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <span className="text-lg">{typeInfo.icon}</span>
                        <h5 className="text-base font-semibold text-gray-900">
                          {key.nickname}
                        </h5>
                        {typeInfo.required ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            Required
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Optional
                          </span>
                        )}
                        {key.isDefault && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-indigo-800">
                            Default
                          </span>
                        )}
                      </div>

                      <div className="mt-2 space-y-1">
                        <div className="flex items-center text-sm text-gray-500">
                          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span>Added {formatDate(key.createdAt)}</span>
                        </div>
                        {key.lastUsedAt && (
                          <div className="flex items-center text-sm text-gray-500">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            <span>Last used {formatDate(key.lastUsedAt)}</span>
                          </div>
                        )}
                      </div>
                    </div>

                    <button
                      onClick={() => handleDelete(key.keyId, key.nickname)}
                      disabled={deleting && deletingKeyId === key.keyId}
                      className="ml-4 p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Delete API key"
                    >
                      {deleting && deletingKeyId === key.keyId ? (
                        <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-gray-400 mt-0.5 mr-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <div className="text-sm text-gray-600">
            <p className="font-medium text-gray-900 mb-1">Security Note</p>
            <p>
              Your API keys are encrypted using AES-256-GCM encryption before being stored.
              We never see or store your plaintext keys. Only encrypted versions are saved in Firebase.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
